import React, { useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useCurrencies, useQueryParams } from "../../utils/hooks";
import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";

import queryString from "query-string";
import ReactPaginate from "react-paginate";
import { paginationOptions } from "../../utils/helpers";
import { LoaderIcon, Logo } from "../Icons";
import currency from "currency.js";
import { toast } from "react-toastify";
import { useStoreState } from "easy-peasy";
import { useNavigate } from "react-router-dom";

import restaurantImage from "../../assets/images/restaurant.png";

function groupItemsByCategory(items) {
  const groupedData = {};
  const allCategories = {};

  items.forEach((item) => {
    // Get top category
    const topCategoryKey = item?.topCategory
      ? item?.topCategory?.toLowerCase()?.replace(/\s+/g, "_")
      : "all";

    // Get category key
    const categoryKey = item?.Cat_Name
      ? item?.Cat_Name?.toLowerCase()?.replace(/\s+/g, "_")
      : "all";

    // Initialize top category if it doesn't exist
    if (!groupedData[topCategoryKey]) {
      groupedData[topCategoryKey] = {};
    }

    // Initialize category under top category if it doesn't exist
    if (!groupedData[topCategoryKey][categoryKey]) {
      groupedData[topCategoryKey][categoryKey] = [];
    }

    const itemData = {
      name: item.Item_Name,
      price: currency(item.UnitPrice, {
        symbol: "",
        precision: 2,
      }).format(),
      barCode: item.Bar_Code,
      topCategory: item?.topCategory || "",
    };

    // Push item to the category
    groupedData[topCategoryKey][categoryKey].push(itemData);

    // Also add to allCategories for the "All" top category
    if (!allCategories[categoryKey]) {
      allCategories[categoryKey] = [];
    }
    allCategories[categoryKey].push(itemData);
  });

  // Create ordered data with "All" appearing first
  const orderedData = {};

  // Add "All" top category with all categories
  if (Object.keys(allCategories).length > 0) {
    orderedData.all = allCategories;
  }

  // Add other categories
  Object.keys(groupedData).forEach((key) => {
    if (key !== "all") {
      orderedData[key] = groupedData[key];
    }
  });

  return orderedData;
}

// Helper function to get category display name
function getCategoryDisplayName(categoryKey) {
  return categoryKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// function getTopCategoryKey() {
//   // array of items
//   //
// }

function getCategoryIcon(categoryKey) {
  // Array of food and drink related emojis
  const foodEmojiOptions = [
    "🍽️",
    "🥤",
    "🍺",
    "🍷",
    "🍸",
    "☕",
    "🍵",
    "🍞",
    "🍩",
    "🍕",
    "🍔",
    "🥗",
    "🍟",
    "🌭",
    "🥪",
    "🌮",
    "🌯",
    "🫔",
    "🥙",
    "🧆",
    "🍖",
    "🍗",
    "🥩",
    "🥓",
    "🍳",
    "🧈",
    "🥞",
    "🧇",
    "🍤",
    "🍣",
    "🍱",
    "🍜",
    "🍲",
    "🫕",
    "🥘",
    "🍛",
    "🍚",
    "🍙",
    "🍘",
    "🍥",
    "🥠",
    "🥮",
    "🍢",
    "🍡",
    "🍧",
    "🍨",
    "🍦",
    "🥧",
    "🧁",
    "🍰",
    "🎂",
    "🍮",
    "🍭",
    "🍬",
    "🍫",
    "🍿",
    "🍪",
    "🥜",
    "🌰",
    "🥨",
  ];

  // Create a simple hash from the category key to ensure consistency
  // Same category will always get the same emoji
  let hash = 0;
  for (let i = 0; i < categoryKey.length; i++) {
    const char = categoryKey.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get a consistent index
  const index = Math.abs(hash) % foodEmojiOptions.length;

  return foodEmojiOptions[index];
}

const MenuPage = () => {
  const navigate = useNavigate();
  const [activeTopCategory, setActiveTopCategory] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const initialFilterParams = {
    page: 1,
    limit: 150, // Increased to get more items
    showOnOnlineStoreMenu: true,
    includeServiceItems: "Inventory",
  };
  const [queryParams] = useQueryParams({
    page: 1,
    limit: 150,
    ...initialFilterParams,
  });

  const { customerBackendUrl: backendUrl } = useAuth();
  const { prevailingCurrencySymbol } = useCurrencies();

  const { data, isFetching } = useQuery(
    [queryParams],
    () => getItems(queryParams),
    {
      keepPreviousData: true,
    },
  );
  const fetchSetUpData = async () => {
    // await waitFor(5000);
    let response = await fetch(`${backendUrl}/api/store/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    return data;
  };

  const { data: settings, isLoading } = useQuery(
    ["STORE_NAV_SET_UP"],
    () => fetchSetUpData(),
    {
      keepPreviousData: true,
    },
  );
  if (!isLoading && !settings?.settings?.enableMenuInOnlineStore) {
    return (
      <div
        className="vw-100 d-flex align-items-center justify-content-center text-center "
        style={{ height: "60vh" }}
      >
        <div>
          <Logo />
          <h4 className="display-5"> Unauthorized</h4>{" "}
          <Button
            onClick={() => navigate(-1)}
            variant=""
            className="text-primary text-underline"
          >
            Go back
          </Button>
        </div>
      </div>
    );
  }

  const getItems = async (debouncedqueryParams) => {
    let response = await fetch(
      `${backendUrl}/api/items?${queryString.stringify(debouncedqueryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();

    const result = groupItemsByCategory(data?.items || []);

    data.menuData = result;

    return data;
  };

  // Use dynamic menu data or fallback to empty object
  const menuData = data?.menuData;

  const MenuItem = ({ item }) => {
    return (
      <div
        className="d-flex align-items-center  px-3 py-2"
        style={{
          borderRadius: "8px",
          transition: "all 0.2s ease",
          cursor: "pointer",
          backgroundColor: "transparent",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f9fafb";
          e.currentTarget.style.transform = "translateX(4px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "translateX(0)";
        }}
      >
        <span
          className="text-dark"
          style={{
            minWidth: "180px",
            fontSize: "15px",
            fontWeight: "500",
            letterSpacing: "0.2px",
          }}
        >
          {item.name}
        </span>
        <div
          className="flex-fill mx-3"
          style={{
            height: "1px",
            backgroundImage:
              "repeating-linear-gradient(to right, #d9d9d9 0, #d9d9d9 2px, transparent 2px, transparent 6px)",
          }}
        />
        <span
          className="fw-bold text-dark"
          style={{
            minWidth: "60px",
            textAlign: "right",
            fontSize: "16px",
            color: "#2463ae",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {prevailingCurrencySymbol} {item.price}
        </span>
      </div>
    );
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: data?.company?.CompName,
        text: `Check out our menu at ${data?.company?.CompName}!`,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success("Link copied to clipboard!");
      });
    }
  };

  const MenuSection = ({ title, items, icon }) => (
    <div className="mb-2" style={{ marginTop: "48px" }}>
      <div className="d-flex align-items-center mb-2">
        <h3
          className="fw-bold text-dark me-3 mb-0"
          style={{
            fontSize: "32px",
            letterSpacing: "-0.3px",
            color: "#1a1a1a",
          }}
        >
          {title}
        </h3>
        {/* <span style={{ fontSize: "28px", opacity: 0.8 }}>{icon}</span> */}
      </div>
      <div
        style={{
          paddingBottom: "8px",
          borderBottom: "2px solid #e8e8e8",
          marginBottom: "24px",
        }}
      />
      <div>
        {items.map((item, index) => (
          <MenuItem key={index} item={item} />
        ))}
      </div>
    </div>
  );

  // Loading state
  if (isFetching && !data) {
    return (
      <div className="global-spinner">
        <LoaderIcon className="spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <style jsx>{`
        .layout-container {
          display: flex;
          height: 100vh;
        }

        .left-panel-static {
          width: 33.333333%;
          height: 100vh;
          overflow: hidden;
          flex-shrink: 0;
        }

        .right-panel-scrollable {
          width: 66.666667%;
          height: 100vh;
          overflow-y: auto;
          flex-shrink: 0;
          background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
          background-attachment: fixed;
        }

        @media (max-width: 768px) {
          .layout-container {
            flex-direction: column;
          }

          .left-panel-static {
            display: none !important;
            width: 0 !important;
          }

          .right-panel-scrollable {
            width: 100% !important;
          }
        }

        .share-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          z-index: 1001;
          border: none;
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          transition: all 0.25s ease;

          cursor: pointer;
          border-radius: 50%;
        }

        .share-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
        }

        .share-btn:active {
          transform: translateY(-1px);
        }

        .geometric-shape {
          position: absolute;
          opacity: 0.15;
          font-weight: bold;
          pointer-events: none;
        }

        .left-shapes .geometric-shape {
          color: #444;
          opacity: 0.2;
        }

        .right-shapes .geometric-shape {
          color: #333;
          opacity: 0.15;
        }

        .shape-star-1 {
          top: 15%;
          left: 15%;
          font-size: 20px;
        }

        .shape-triangle-1 {
          top: 25%;
          right: 20%;
          font-size: 24px;
        }

        .shape-diamond-1 {
          bottom: 40%;
          left: 20%;
          font-size: 18px;
        }

        .shape-star-2 {
          bottom: 20%;
          left: 60%;
          font-size: 16px;
        }

        .shape-star-3 {
          top: 8%;
          right: 15%;
          font-size: 20px;
        }

        .shape-triangle-2 {
          top: 35%;
          right: 8%;
          font-size: 22px;
        }

        .shape-diamond-2 {
          bottom: 45%;
          right: 12%;
          font-size: 18px;
        }

        .shape-star-4 {
          bottom: 15%;
          right: 20%;
          font-size: 16px;
        }

        .shape-oval-1 {
          top: 60%;
          left: 5%;
          font-size: 14px;
          transform: rotate(45deg);
        }

        .shape-oval-2 {
          bottom: 8%;
          left: 15%;
          font-size: 16px;
          transform: rotate(-20deg);
        }

        .empty-menu {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .empty-menu h3 {
          margin-bottom: 10px;
          color: #999;
        }
        @media (max-width: 768px) {
          .left-panel-static {
            display: none important!;
          }

          .right-panel-scrollable {
            width: 100% important!;
          }
        }
      `}</style>

      <div
        className="layout-container"
        style={{
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        }}
      >
        {/* Static Left Panel */}
        <div
          className="left-panel-static bg-dark d-flex flex-column justify-content-between position-relative"
          // style={{ background: "#000" }}
          style={{
            backgroundImage: `url(${restaurantImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 1,
            }}
          />
          <div
            className="d-flex flex-column justify-content-between position-relative"
            style={{ height: "100%", justifyContent: "space-between" }}
          >
            <div className="p-4 pt-5">
              <h1
                className="text-white fw-bold mb-0"
                style={{ fontSize: "48px", lineHeight: "1.1" }}
              >
                {data?.company?.CompName}
              </h1>
            </div>

            <div className="p-4 pb-4 fixed-bottom bottom-0">
              <div
                className="text-white"
                style={{ fontSize: "14px", lineHeight: "1.4" }}
              >
                <div className="mb-1"> {data?.company?.AddressLine1}</div>
                <div className="mb-1">{data?.company?.AddressLine2}</div>
                <div>{data?.company?.Phone}</div>
              </div>
            </div>
          </div>

          <div className="left-shapes">
            <div className="geometric-shape shape-star-1">
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-triangle-1">
              ▲{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-diamond-1">
              ◆{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-star-2">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
          </div>
        </div>

        {/* Scrollable Right Panel */}
        <div
          className="right-panel-scrollable position-relative"
          // style={{ background: "#f8f8f8" }}
        >
          {/* Share Button */}

          <div className="right-shapes">
            <div className="geometric-shape shape-star-3">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-triangle-2">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ▲
            </div>
            <div className="geometric-shape shape-diamond-2">
              ◆{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-star-4">
              {" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
              ✱
            </div>
            <div className="geometric-shape shape-oval-1">
              ⬬{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
            <div className="geometric-shape shape-oval-2">
              ⬬{" "}
              <img
                src={`${backendUrl}/api/users/logo/company`}
                alt=""
                width={100}
                height={100}
              />{" "}
            </div>
          </div>

          <div className="px-4 position-relative">
            <div
              className=" px-4"
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#ffffff",
                zIndex: 100,
                // paddingTop: "0px",
                // paddingBottom: "24px",
                marginBottom: "10px",
                borderBottom: "2px solid #e8e8e8",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              }}
            >
              <div
                className="d-flex align-items-center mb-4"
                style={{ paddingTop: "40px" }}
              >
                <h2
                  className="fw-bold text-dark me-3 mb-0"
                  style={{ fontSize: "52px", letterSpacing: "-0.5px" }}
                >
                  Menu
                </h2>

                <style jsx>{`
                  @media (max-width: 768px) {
                    h2 {
                      font-size: 28px !important;
                    }
                    h3 {
                      font-size: 20px !important;
                    }
                  }
                `}</style>
                <span style={{ fontSize: "32px" }}>🍴</span>
              </div>

              {/* Top Category Buttons */}
              {menuData && Object.keys(menuData).length > 0 && (
                <div>
                  <div className="d-flex flex-wrap gap-3 mb-4">
                    {Object.keys(menuData).map((topCategoryKey) => (
                      <button
                        key={topCategoryKey}
                        onClick={() => {
                          setActiveTopCategory(topCategoryKey);
                          setActiveCategory(null);
                        }}
                        style={{
                          padding: "12px 28px",
                          fontSize: "15px",
                          fontWeight:
                            activeTopCategory === topCategoryKey
                              ? "700"
                              : "600",
                          border: "none",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "all 0.25s ease",
                          backgroundColor:
                            activeTopCategory === topCategoryKey
                              ? "#2463ae"
                              : "#f5f5f5",
                          color:
                            activeTopCategory === topCategoryKey
                              ? "#fff"
                              : "#2463ae",
                          boxShadow:
                            activeTopCategory === topCategoryKey
                              ? "0 6px 16px rgba(36, 99, 174, 0.24)"
                              : "0 1px 3px rgba(0, 0, 0, 0.08)",
                          outline: "none",
                          textTransform: "capitalize",
                          letterSpacing: "0.3px",
                        }}
                        onMouseEnter={(e) => {
                          if (activeTopCategory !== topCategoryKey) {
                            e.target.style.backgroundColor = "#ececec";
                            e.target.style.boxShadow =
                              "0 2px 6px rgba(0, 0, 0, 0.12)";
                          } else {
                            e.target.style.boxShadow =
                              "0 8px 20px rgba(36, 99, 174, 0.32)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeTopCategory !== topCategoryKey) {
                            e.target.style.backgroundColor = "#f5f5f5";
                            e.target.style.boxShadow =
                              "0 1px 3px rgba(0, 0, 0, 0.08)";
                          } else {
                            e.target.style.boxShadow =
                              "0 6px 16px rgba(36, 99, 174, 0.24)";
                          }
                        }}
                      >
                        {getCategoryDisplayName(topCategoryKey)}
                      </button>
                    ))}
                  </div>

                  {/* Category Buttons with Horizontal Scroll */}
                  {activeTopCategory && menuData[activeTopCategory] && (
                    <div
                      className="mb-4"
                      style={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        paddingBottom: "12px",
                        scrollBehavior: "smooth",
                      }}
                    >
                      <div
                        className="d-flex gap-3"
                        style={{ minWidth: "max-content" }}
                      >
                        {Object.keys(menuData[activeTopCategory]).map(
                          (categoryKey) => (
                            <button
                              key={categoryKey}
                              onClick={() => {
                                setActiveCategory(categoryKey);
                                const element = document.getElementById(
                                  `${activeTopCategory}-${categoryKey}`,
                                );
                                element?.scrollIntoView({ behavior: "smooth" });
                              }}
                              className=" text-primary"
                              style={{
                                padding: "10px 22px",
                                fontSize: "13px",
                                fontWeight: "600",
                                border: "1.5px solid #d9d9d9",
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "all 0.25s ease",
                                backgroundColor: "#fff",

                                whiteSpace: "nowrap",
                                textTransform: "capitalize",
                                letterSpacing: "0.2px",
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.borderColor = "#2463ae";
                                e.target.style.color = "#2463ae";
                                e.target.style.boxShadow =
                                  "0 4px 12px rgba(36, 99, 174, 0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.borderColor = "#d9d9d9";
                                e.target.style.color = "#333";
                                e.target.style.boxShadow =
                                  "0 1px 2px rgba(0, 0, 0, 0.04)";
                              }}
                            >
                              {getCategoryDisplayName(categoryKey)}
                            </button>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button
                className="share-btn"
                onClick={handleShare}
                title="Share Menu"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              </Button>
            </div>

            {/* Dynamic Menu Sections */}
            {menuData && Object.keys(menuData).length > 0 ? (
              Object.entries(menuData).map(([topCategoryKey, categories]) => {
                // Show all sections if no specific category is selected, or if it's the "all" top category
                // Otherwise, only show the selected category
                if (
                  activeCategory &&
                  activeTopCategory !== "all" &&
                  topCategoryKey === activeTopCategory
                ) {
                  return (
                    <div key={topCategoryKey} id={topCategoryKey}>
                      {Object.entries(categories)
                        .filter(
                          ([categoryKey]) => categoryKey === activeCategory,
                        )
                        .map(([categoryKey, items]) => {
                          const displayName = getCategoryDisplayName(
                            categoryKey,
                          );
                          return (
                            <div
                              key={categoryKey}
                              id={`${topCategoryKey}-${categoryKey}`}
                              style={{ scrollMarginTop: "400px" }}
                            >
                              <MenuSection
                                title={displayName}
                                items={items}
                                icon={getCategoryIcon(categoryKey, displayName)}
                              />
                            </div>
                          );
                        })}
                    </div>
                  );
                }

                // Show all categories when "All" is selected or no specific category selected
                if (!activeCategory || activeTopCategory === "all") {
                  return (
                    <div key={topCategoryKey} id={topCategoryKey}>
                      {Object.entries(categories).map(
                        ([categoryKey, items]) => {
                          const displayName = getCategoryDisplayName(
                            categoryKey,
                          );
                          return (
                            <div
                              key={categoryKey}
                              id={`${topCategoryKey}-${categoryKey}`}
                              style={{ scrollMarginTop: "400px" }}
                            >
                              <MenuSection
                                title={displayName}
                                items={items}
                                icon={getCategoryIcon(categoryKey, displayName)}
                              />
                            </div>
                          );
                        },
                      )}
                    </div>
                  );
                }

                return null;
              })
            ) : (
              <div
                className="empty-menu text-center"
                style={{
                  paddingTop: "80px",
                  paddingBottom: "80px",
                }}
              >
                <div
                  style={{
                    fontSize: "64px",
                    marginBottom: "24px",
                    opacity: 0.6,
                  }}
                >
                  📋
                </div>
                <h3
                  className="fw-bold text-dark"
                  style={{ fontSize: "28px", marginBottom: "12px" }}
                >
                  No menu items available
                </h3>
                <p
                  className="text-muted"
                  style={{ fontSize: "16px", marginBottom: "0" }}
                >
                  Please check back later or contact us for more information.
                </p>
              </div>
            )}

            {/* Loading indicator for updates */}
            {isFetching && data && (
              <div className="global-spinner">
                <LoaderIcon className="spin text-primary" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuPage;
